import styled from '@emotion/styled';
import React, { FC, useState, useMemo, useCallback } from 'react';
import { Box, Button, GreenButton, Input, SearchInputWrapper } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useDebounce, useI18n, useSelector, useLogger } from '~Hooks';
import { CompleteBongQuery_venue_company_items } from '~Scenes/POS/Queries/__queries__';
import { useInventoryApi } from '~Components/InventoryUniverse';
import { useInventoryUniverse } from '~Components/InventoryUniverse';
import BatchProductRow from './BatchProductRow';
import { isServiceChargeItem } from '~Utils/serviceCharge';

const ModalContent = styled.div`
  padding: 24px;
  background: var(--colorBlueGray1200);
  border-radius: 8px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.2);
  width: 600px;
  max-width: calc(100% - 64px);
  margin: 0;
  height: fit-content;
  max-height: calc(100vh - 16px);
  overflow-y: auto;

  /** Push modal up so iPad keyboard doesn't cover the input field */
  @media (min-height: 496px) {
    max-height: none;
    overflow-y: visible;
    margin: 16px 0 0 0;
  }

  @media (min-height: 680px) {
    margin: 16px 0 10% 0;
  }
`;

const ResultsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  margin-top: 16px;
`;

interface ProductUpdate {
  count: number;
  override: boolean;
}

interface Props {
  items: CompleteBongQuery_venue_company_items[];
  onClose: () => void;
}

const BatchUpdateProducts: FC<Props> = ({ items, onClose }) => {
  const { i18n } = useI18n();
  const logger = useLogger('batch-update-products');
  const venueId = useSelector((state) => state.venueId);
  const inventoryApi = useInventoryApi(venueId);
  const inventory = useInventoryUniverse();

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Track updates per product: { [itemId]: { count, override } }
  const [updates, setUpdates] = useState<Record<string, ProductUpdate>>({});

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Only show products when there's a search term
  const filteredItems = useMemo(() => {
    if (!debouncedSearchTerm) return [];

    const query = debouncedSearchTerm.toLowerCase();
    return items
      .filter((item) => {
        const title = i18n(item.titleLang).toLowerCase();
        if (isServiceChargeItem(title) || title.includes('[m]')) {
          return false;
        }
        return title.includes(query);
      })
      .sort((a, b) => i18n(a.titleLang).localeCompare(i18n(b.titleLang)));
  }, [items, debouncedSearchTerm, i18n]);

  // Get inventory status for an item
  const getInventoryStatus = useCallback(
    (itemId: string) => {
      const item = inventory.items.find((i) => i.itemId === itemId);
      if (item) {
        return { count: item.count, outOfStock: item.outOfStock };
      }
      return { count: null, outOfStock: false };
    },
    [inventory],
  );

  // Get or create update entry for a product
  const getUpdate = (itemId: string): ProductUpdate => {
    return updates[itemId] ?? { count: 0, override: false };
  };

  const setUpdate = (itemId: string, update: ProductUpdate) => {
    setUpdates((prev) => ({ ...prev, [itemId]: update }));
  };

  const handleIncrement = (itemId: string) => {
    const current = getUpdate(itemId);
    setUpdate(itemId, { ...current, count: current.count + 1 });
  };

  const handleDecrement = (itemId: string) => {
    const current = getUpdate(itemId);
    const status = getInventoryStatus(itemId);

    if (current.override) {
      // Override mode: can't go below 0
      if (current.count > 0) {
        setUpdate(itemId, { ...current, count: current.count - 1 });
      }
    } else {
      // Add/subtract mode: can go negative down to -currentCount
      const minCount = status.count != null ? -status.count : 0;
      if (current.count > minCount) {
        setUpdate(itemId, { ...current, count: current.count - 1 });
      }
    }
  };

  const handleToggleOverride = (itemId: string, isActive: boolean) => {
    const current = getUpdate(itemId);
    setUpdate(itemId, { ...current, override: isActive });
  };

  // Count how many products have pending updates (count !== 0)
  const pendingUpdateCount = useMemo(() => {
    return Object.values(updates).filter((u) => u.count !== 0).length;
  }, [updates]);

  const cancelButton = useButton({
    onClick: onClose,
  });

  const submitButton = useButton({
    onClick: () => handleSubmit(),
    disabled: pendingUpdateCount === 0,
  });

  // Submit all pending updates
  const handleSubmit = () => {
    Object.entries(updates).forEach(([itemId, update]) => {
      if (update.count === 0) return;

      const status = getInventoryStatus(itemId);

      if (update.override) {
        // Override: set stock to exact count value
        const currentCount = status.count ?? 0;
        const diff = update.count - currentCount;

        if (diff > 0) {
          inventoryApi.addStock(itemId, diff);
        } else if (diff < 0) {
          inventoryApi.reduceStock(itemId, Math.abs(diff), 'Batch override');
        }
      } else if (update.count > 0) {
        // Positive: add stock on top of current
        inventoryApi.addStock(itemId, update.count);
      } else {
        // Negative: reduce stock
        inventoryApi.reduceStock(itemId, Math.abs(update.count), 'Batch update');
      }
    });

    logger.info('Batch update submitted', { updateCount: pendingUpdateCount });
    onClose();
  };

  return (
    <ModalContent>
      <Heading level={2} color="var(--colorWhite)" bottom={8}>
        {i18n('Inventory.BatchUpdateInventory')}
      </Heading>
      <Paragraph level={4} color="var(--colorBlueGray200)">
        {i18n('Inventory.BatchUpdateDescription')}
      </Paragraph>

      <Box marginTop="small">
        <SearchInputWrapper fluid value={searchTerm} onClear={() => setSearchTerm('')}>
          <Input
            id="batch-search"
            name="batch-search"
            placeholder={i18n('Inventory.SearchProducts')}
            value={searchTerm}
            onChange={handleSearchChange}
            dark
            padding="small"
            isSearch
            autoFocus
            fluid
          />
        </SearchInputWrapper>
      </Box>

      {filteredItems.length > 0 && (
        <ResultsList>
          {filteredItems.map((item) => {
            const status = getInventoryStatus(item.id);
            const update = getUpdate(item.id);

            return (
              <BatchProductRow
                key={item.id}
                name={i18n(item.titleLang)}
                currentCount={status.count}
                isOutOfStock={status.outOfStock}
                newCount={update.count}
                overrideEnabled={update.override}
                onIncrement={() => handleIncrement(item.id)}
                onDecrement={() => handleDecrement(item.id)}
                onToggleOverride={(isActive) => handleToggleOverride(item.id, isActive)}
              />
            );
          })}
        </ResultsList>
      )}

      {debouncedSearchTerm && filteredItems.length === 0 && (
        <Box marginTop="medium">
          <Paragraph level={4} color="var(--colorBlueGray200)">
            {i18n('Inventory.NoProductsFoundMatching')} "{debouncedSearchTerm}"
          </Paragraph>
        </Box>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" marginTop="large">
        <Paragraph level={4} color="var(--colorBlueGray200)">
          {pendingUpdateCount > 0
            ? `${pendingUpdateCount} ${
                pendingUpdateCount === 1
                  ? i18n('Inventory.ProductsWillBeUpdated')
                  : i18n('Inventory.ProductsWillBeUpdatedPlural')
              }`
            : i18n('Inventory.NoProductsSelectedForUpdate')}
        </Paragraph>
        <Box display="flex" gap="small">
          <Button {...cancelButton} padding="small">
            {i18n('General.Cancel')}
          </Button>
          <GreenButton {...submitButton} padding="small">
            {i18n('Inventory.UpdateInventory')}
          </GreenButton>
        </Box>
      </Box>
    </ModalContent>
  );
};

export default BatchUpdateProducts;
